import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { Search } from "lucide-react";
import { getAuth } from "firebase/auth";

import { useCustomTheme } from "../../contexts/CustomThemeContext";

export interface SearchableUser {
  id: string;
  uid?: string;
  name: string;
  displayName?: string;
  email: string;
  photoURL?: string;
  role?: string;
  [key: string]: unknown;
}

interface UserSearchDropdownProps {
  onSelect: (user: SearchableUser) => void | Promise<void>;
}

const rawApiUrl = "http://localhost:5000/api/v1";

const API_BASE_URL = String(rawApiUrl).replace(/\/$/, "");

const cardsBorderRadii = "8px";
const UserSearchDropdown = ({ onSelect }: UserSearchDropdownProps) => {
  const { theme } = useCustomTheme();
  const toast = useToast();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchableUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
    };
  }, []);

  useEffect(() => {
    const trimmedQuery = query.trim();

    if (trimmedQuery.length < 2) {
      setResults([]);
      setOpen(false);
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    const timer = window.setTimeout(async () => {
      setLoading(true);

      try {
        const currentUser = getAuth().currentUser;

        if (!currentUser) {
          throw new Error("You must be signed in.");
        }

        const token = await currentUser.getIdToken();
        const response = await fetch(
          `${API_BASE_URL}/user-directory?search=${encodeURIComponent(
            trimmedQuery,
          )}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
          },
        );

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            payload?.message ||
              payload?.error ||
              "Users could not be searched.",
          );
        }

        const users = Array.isArray(payload?.users)
          ? payload.users
          : Array.isArray(payload?.data)
            ? payload.data
            : [];

        const normalizedUsers: SearchableUser[] = users.map(
          (candidate: Record<string, unknown>) => {
            const uid = String(candidate.uid || candidate.id || "");
            const displayName = String(
              candidate.displayName || candidate.name || candidate.email || uid,
            );

            return {
              ...candidate,
              id: uid,
              uid,
              name: displayName,
              displayName,
              email: String(candidate.email || ""),
              photoURL: candidate.photoURL
                ? String(candidate.photoURL)
                : undefined,
              role: candidate.role ? String(candidate.role) : undefined,
            };
          },
        );

        setResults(normalizedUsers);
        setOpen(true);
      } catch (error) {
        if ((error as Error).name === "AbortError") {
          return;
        }

        console.error("User search failed:", error);
        setResults([]);
        setOpen(false);

        toast({
          title: "User search failed",
          description:
            error instanceof Error ? error.message : "Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query, toast]);

  const chooseUser = async (selectedUser: SearchableUser) => {
    try {
      await onSelect(selectedUser);
      setQuery("");
      setResults([]);
      setOpen(false);
    } catch (error) {
      console.error("Failed to select user:", error);
    }
  };

  return (
    <Box ref={containerRef} position="relative" width="100%">
      <InputGroup>
        <InputLeftElement pointerEvents="none">
          {loading ? <Spinner size="sm" /> : <Search size={16} />}
        </InputLeftElement>

        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => {
            if (results.length > 0) {
              setOpen(true);
            }
          }}
          placeholder="Search by name, email, or user ID"
          bg={theme.inputBg}
          color={theme.text}
          borderColor={theme.inputBorder}
          _placeholder={{ color: theme.inputPlaceholder }}
          _hover={{ borderColor: theme.borderStrong }}
          _focus={{
            borderColor: theme.inputBorderFocus,
            boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
          }}
        />
      </InputGroup>

      {open && (
        <VStack
          position="absolute"
          top="calc(100% + 8px)"
          left={0}
          right={0}
          zIndex={30}
          spacing={0}
          align="stretch"
          maxH="320px"
          overflowY="auto"
          bg={theme.surface}
          border="1px solid"
          borderColor={theme.border}
          borderRadius={cardsBorderRadii}
          boxShadow={theme.shadowMd}
        >
          {results.length === 0 ? (
            <Text p={4} color={theme.textMuted}>
              No matching users found.
            </Text>
          ) : (
            results.map((result) => (
              <HStack
                key={result.id}
                p={3}
                cursor="pointer"
                _hover={{ bg: theme.hover }}
                onClick={() => chooseUser(result)}
                borderBottom="1px solid"
                borderColor={theme.border}
                _last={{ borderBottom: "none" }}
              >
                <Avatar size="sm" src={result.photoURL} name={result.name} />

                <Box minW={0}>
                  <Text fontWeight="700" color={theme.text} noOfLines={1}>
                    {result.name}
                  </Text>

                  <Text fontSize="sm" color={theme.textMuted} noOfLines={1}>
                    {result.email || result.id}
                  </Text>
                </Box>
              </HStack>
            ))
          )}
        </VStack>
      )}
    </Box>
  );
};

export default UserSearchDropdown;
